import { MutableRefObject, useCallback, useRef, useState } from 'react';
import { LIFE_INSURANCE_STEPS } from '@app/app/esales/life/_sections/steps/LIFE_INSURANCE_STEPS';
import { LifePackageCode } from '@app/domain/model/eshop/life/LifePackageCode';
import { LifeEditContext } from '@app/domain/model/eshop/life/LifeEditContext';

interface Props {
    selectedPackage?: string;
}

interface UseEditControllerReturn {
    isEditMode: boolean;
    returnToSummary: boolean;
    summaryStepToReturn: Nullable<string>;
    activeEditContext: Nullable<LifeEditContext>;
    setCurrentStepKeyRef: MutableRefObject<Nullable<(key: string) => void>>;

    handleEditDefenses: (step?: string) => void;
    handleEditContractInfo: (step?: string) => void;
    handleEditContractDuration: (step?: string) => void;
    handleEditPaymentSchedule: (step?: string) => void;
    handleEditContactInfo: (step?: string) => void;
    handleEditTaxInformation: (step?: string) => void;
    handleEditLifestyleAndHealth: (step?: string) => void;
    handleEditBeneficiaries: (step?: string) => void;

    shouldReturnToSummary: (currentStepKey: string, selectedPackage?: string) => boolean;
    resetEditMode: () => void;
    setCurrentStepKeyCallback: (callback: (key: string) => void) => void;
}

export function useEditController({ selectedPackage }: Props): UseEditControllerReturn {
    const [returnToSummary, setReturnToSummary] = useState(false);
    const [summaryStepToReturn, setSummaryStepToReturn] = useState<Nullable<string>>(null);
    const [activeEditContext, setActiveEditContext] = useState<Nullable<LifeEditContext>>(null);
    const setCurrentStepKeyRef = useRef<Nullable<(key: string) => void>>(null);

    const handleEditDefenses = useCallback(
        (step?: string) => {
            const stepKey =
                selectedPackage === LifePackageCode.Free
                    ? LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.CUSTOM_COVERS
                    : LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.CHOOSE_PACKAGE;
            setCurrentStepKeyRef.current?.(stepKey);
            setReturnToSummary(true);
            setActiveEditContext(LifeEditContext.Defenses);
            setSummaryStepToReturn(step || LIFE_INSURANCE_STEPS.SUMMARY);
        },
        [selectedPackage],
    );

    const handleEditContractInfo = useCallback((step?: string) => {
        setReturnToSummary(true);
        setActiveEditContext(LifeEditContext.ContractInfo);
        setSummaryStepToReturn(step || LIFE_INSURANCE_STEPS.SUMMARY);
        setCurrentStepKeyRef.current?.(LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.DURATION);
    }, []);

    const handleEditContractDuration = useCallback((step?: string) => {
        setReturnToSummary(true);
        setActiveEditContext(LifeEditContext.ContractDuration);
        setSummaryStepToReturn(step || LIFE_INSURANCE_STEPS.SUMMARY);
        setCurrentStepKeyRef.current?.(LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.DURATION);
    }, []);

    const handleEditPaymentSchedule = useCallback((step?: string) => {
        setReturnToSummary(true);
        setActiveEditContext(LifeEditContext.PaymentSchedule);
        setSummaryStepToReturn(step || LIFE_INSURANCE_STEPS.SUMMARY);
        setCurrentStepKeyRef.current?.(LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.PAYMENT_SCHEDULE);
    }, []);

    const handleEditContactInfo = useCallback((step?: string) => {
        setReturnToSummary(true);
        setActiveEditContext(LifeEditContext.ContactInfo);
        setSummaryStepToReturn(step || LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.OVERVIEW_OF_CONTRACT);
        setCurrentStepKeyRef.current?.(LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HALFWAY_LOGIN);
    }, []);

    const handleEditTaxInformation = useCallback((step?: string) => {
        setReturnToSummary(true);
        setActiveEditContext(LifeEditContext.TaxInformation);
        setSummaryStepToReturn(step || LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.OVERVIEW_OF_CONTRACT);
        setCurrentStepKeyRef.current?.(LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.TAX_INFORMATION);
    }, []);

    const handleEditLifestyleAndHealth = useCallback((step?: string) => {
        setReturnToSummary(true);
        setActiveEditContext(LifeEditContext.LifestyleAndHealth);
        setSummaryStepToReturn(step || LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.OVERVIEW_OF_CONTRACT);
        setCurrentStepKeyRef.current?.(LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.PROFESSION);
    }, []);

    const handleEditBeneficiaries = useCallback((step?: string) => {
        setReturnToSummary(true);
        setActiveEditContext(LifeEditContext.Beneficiaries);
        setSummaryStepToReturn(step || LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.OVERVIEW_OF_CONTRACT);
        setCurrentStepKeyRef.current?.(LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.BENEFICIARIES);
    }, []);

    const shouldReturnToSummary = useCallback(
        (currentStepKey: string, currentSelectedPackage?: string): boolean => {
            if (!returnToSummary || !activeEditContext) {
                return false;
            }

            if (activeEditContext === LifeEditContext.Defenses) {
                const isChoosePackageStepWithNonFreePackage =
                    currentStepKey === LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.CHOOSE_PACKAGE &&
                    currentSelectedPackage !== LifePackageCode.Free;
                const isCustomCoversStep = currentStepKey === LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.CUSTOM_COVERS;

                return isChoosePackageStepWithNonFreePackage || isCustomCoversStep;
            }

            if (activeEditContext === LifeEditContext.ContractDuration) {
                return currentStepKey === LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.DURATION;
            }

            if (activeEditContext === LifeEditContext.PaymentSchedule) {
                return currentStepKey === LIFE_INSURANCE_STEPS.PACKAGE_AND_COVERAGES.PAYMENT_SCHEDULE;
            }

            if (activeEditContext === LifeEditContext.ContactInfo) {
                return currentStepKey === LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.CONTACT_DETAILS;
            }

            if (activeEditContext === LifeEditContext.TaxInformation) {
                return currentStepKey === LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.TAX_INFORMATION;
            }

            if (activeEditContext === LifeEditContext.LifestyleAndHealth) {
                return currentStepKey === LIFE_INSURANCE_STEPS.LIFESTYLE_AND_HEALTH.HEALTH_QUESTIONS;
            }

            if (activeEditContext === LifeEditContext.Beneficiaries) {
                return currentStepKey === LIFE_INSURANCE_STEPS.SIGNING_THE_CONTRACT.BENEFICIARIES;
            }

            return false;
        },
        [returnToSummary, activeEditContext],
    );

    const resetEditMode = useCallback(() => {
        setReturnToSummary(false);
        setSummaryStepToReturn(null);
        setActiveEditContext(null);
    }, []);

    const setCurrentStepKeyCallback = useCallback((callback: (key: string) => void) => {
        setCurrentStepKeyRef.current = callback;
    }, []);

    return {
        isEditMode: returnToSummary,
        returnToSummary,
        summaryStepToReturn,
        activeEditContext,
        setCurrentStepKeyRef,

        handleEditDefenses,
        handleEditContractInfo,
        handleEditContractDuration,
        handleEditPaymentSchedule,
        handleEditContactInfo,
        handleEditTaxInformation,
        handleEditLifestyleAndHealth,
        handleEditBeneficiaries,

        shouldReturnToSummary,
        resetEditMode,
        setCurrentStepKeyCallback,
    };
}
